import { AssetLibrary } from '#base/assetLibrary.js';
import { Font } from '#base/fonts.js';
import { textToDataUrl } from '#base/text.js';

type DrawFunc =
  | ((assetLibrary: AssetLibrary, e: Element) => string)
  | ((assetLibrary: AssetLibrary, e: Element) => string | undefined);

const elementRegistry = new Map<string, DrawFunc>();

// When adding a canvas element, if it is used by multiple apps (e.g. Designer and ProofDesigner),
// then register it here. However, if it is specific to one app, provide that urlFor... call and
// register it in that component, and it will be automatically used.

export const registerCanvasElement = (elementType: string, drawFunc: DrawFunc) => {
  if (elementRegistry.has(elementType)) {
    console.error(`Canvas element ${elementType} has already been registered`);
  }
  elementRegistry.set(elementType, drawFunc);
  return elementType;
};

export const urlForCanvasElement = (assetLibrary: AssetLibrary, e: Element) => {
  return elementRegistry.get(e.elementType)?.(assetLibrary, e);
};

export const urlForArtwork = (assetLibrary: AssetLibrary, e: Element) => {
  return assetLibrary.artwork.find((f) => f.key === e.data)?.def?.file;
};

export const urlForImage = (assetLibrary: AssetLibrary, e: Element) => {
  return e.url;
};

export const urlForPreview = (assetLibrary: AssetLibrary, e: Element) => {
  return e.dataUrl;
};

export const urlForText = (assetLibrary: AssetLibrary, e: Element) => {
  const type = (e.elementType !== 'title' ? e.elementType : 'font') as keyof typeof assetLibrary; // 'monogram' and 'jersey' are also asset types
  return textToDataUrl(
    e.data,
    Object.assign({}, e, {
      font: assetLibrary[type].find((f) => f.key === e.fontName || f.key === e.assetId) as Font,
    })
  );
};

// The calls to register() get called multiple times, this prevents the reregistration error
// while still allowing the check when registered elsewhere.
const register = (elementType: string, f: DrawFunc) => {
  if (!elementRegistry.has(elementType)) {
    registerCanvasElement(elementType, f);
  }
};
register('font', urlForText);
register('title', urlForText);
register('monogram', urlForText);
register('jersey', urlForText);
register('artwork', urlForArtwork);
register('file', urlForImage);
register('uploaded', urlForImage);
register('preview', urlForPreview);

// TODO: Find out if this is necessary. If not, can remove globalThis dep
export const setGlobalElementRegistry = () => {
  if (!globalThis._gElementRegistry) {
    globalThis._gElementRegistry = elementRegistry;
  }
};

export type Element = {
  elementType: string;
  data: string;
  url: string;
  dataUrl: string;
  fontName: string;
  assetId: string;
  width: number;
  height: number;
  color: string;
};
